import { Reorder, useDragControls } from 'framer-motion';

import type { LobbyGender } from '@/ui/GenderToggle';
import { GenderToggle } from '@/ui/GenderToggle';
import { cx } from '@/ui/cx';

export interface DraftPlayer {
  /** Ключ для Reorder/React — не участвует в запросе, только в разметке. */
  key: string;
  name: string;
  gender: LobbyGender;
}

export interface PlayerRowProps {
  player: DraftPlayer;
  dragging: boolean;
  onChange: (next: DraftPlayer) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

/**
 * Строка 58px: ручка ≡, имя, переключатель Ж/М (design-spec.md, 02 · Настройка
 * партии). Тащить можно только за ручку — `dragListener={false}` снимает
 * перетаскивание со всей строки, иначе тап по полю имени или по Ж/М начинал бы
 * жест вместо своего дела.
 *
 * Строка под пальцем подсвечивается снаружи (родитель знает, кого тащат) —
 * `panel-hi` с outline 2px acc внутрь, ровно как в макете.
 */
export function PlayerRow({ player, dragging, onChange, onDragStart, onDragEnd }: PlayerRowProps) {
  const controls = useDragControls();

  return (
    <Reorder.Item
      value={player}
      dragListener={false}
      dragControls={controls}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cx(
        'flex h-row items-center gap-3 bg-panel px-4',
        dragging && 'bg-panel-hi outline outline-2 -outline-offset-2 outline-acc',
      )}
    >
      <button
        type="button"
        aria-label="Перетащить, чтобы изменить порядок"
        onPointerDown={(event) => controls.start(event)}
        className="flex h-full min-w-tap shrink-0 appearance-none items-center justify-center rounded-none text-lead text-mute"
      >
        ≡
      </button>

      <input
        value={player.name}
        onChange={(event) => onChange({ ...player, name: event.target.value })}
        placeholder="Имя"
        maxLength={32}
        aria-label="Имя игрока"
        className="min-w-0 flex-1 appearance-none rounded-none bg-transparent text-row text-ink outline-none placeholder:text-mute"
      />

      <GenderToggle value={player.gender} onChange={(gender) => onChange({ ...player, gender })} />
    </Reorder.Item>
  );
}
